import { useEffect, useState, useRef, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ExcelJS from "exceljs";
import { getDemandText } from "../utils/demandText";
import ListWorkforce from "./ListWorkforce";
import { ProductsContext } from "../context/products.context";
// AnimatedHeight Component (unchanged)
const AnimatedHeight = ({ isOpen, children }) => {
  const [height, setHeight] = useState(isOpen ? "auto" : 0);
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      setHeight(isOpen ? ref.current.scrollHeight : 0);
    }
  }, [isOpen, children]);

  return (
    <motion.div
      initial={{ height: 0 }}
      animate={{ height }}
      exit={{ height: 0 }}
      style={{ overflow: "hidden" }}
    >
      <div ref={ref}>{children}</div>
    </motion.div>
  );
};

const visibleLimit = 4;

export const CipsElement = ({ items, resourceName, itemComponent: ItemComponent }) => {
  const [expanded, setExpanded] = useState(false);
  const { setWorkforce } = useContext(ProductsContext); // Access setWorkforce from context
  const changeFilter = (newFilter) => {
    setWorkforce(newFilter);
  };
  // For the “Copied!” message
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  if (!items || items.length === 0) {
    return null;
  }

  // -------------------------------
  // 1) Copy Items to Clipboard
  // -------------------------------
  const copyItemsToClipboard = async () => {
    try {
      if (!items || !items.length) {
        console.warn("No items to copy");
        return;
      }

      // For currency
      const wageFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });
      // For commas in integers
      const intFormatter = new Intl.NumberFormat("en-US");

      // Build each row
      const rows = items.map((job) => {
        // Demand
        const demandStatus = getDemandText(job.maxDemand) ?? "";

        // Raw SOC (no dash, to avoid Excel's date issue)
        const socString = job.onetSOC ?? "";

        // Title
        const title = job.onetTitle ?? "";

        // Wages (formatted)
        const annualWage =
          job.avgAnnualWage != null ? wageFormatter.format(job.avgAnnualWage) : "";
        const hourlyWage =
          job.avgHourlyWage != null ? wageFormatter.format(job.avgHourlyWage) : "";

        // Openings (commas)
        const openings =
          job.avgTotalOpenings != null
            ? intFormatter.format(job.avgTotalOpenings)
            : "";

        // Employment (commas)
        const employment =
          job.estimatedEmployment != null
            ? intFormatter.format(job.estimatedEmployment)
            : "";

        // Growth Rate (##.##%)
        let growthRate = "";
        if (job.longtermGrowth != null) {
          const pct = (job.longtermGrowth * 100).toFixed(2);
          growthRate = `${pct}%`;
        }

        // Education
        const typicalEd = job.education ?? "";

        return {
          "Demand Status": demandStatus,
          "SOC": socString,
          "Title": title,
          "Annual Wage": annualWage,
          "Hourly Wage": hourlyWage,
          "Openings": openings,
          "Employment": employment,
          "Growth Rate": growthRate,
          "Education": typicalEd,
        };
      });

      // Convert rows to tab-delimited text
      const header = Object.keys(rows[0]).join("\t");
      const dataLines = rows.map((row) => Object.values(row).join("\t"));
      const fullText = [header, ...dataLines].join("\n");

      // Copy to clipboard
      await navigator.clipboard.writeText(fullText);

      // Show success message for 5 seconds
      setShareLinkCopied(true);
      setTimeout(() => {
        setShareLinkCopied(false);
      }, 3000);

    } catch (error) {
    }
  };

  // -------------------------------
  // 2) Export Items to Excel
  // -------------------------------
  const exportItemsToExcel = async () => {
    if (!items.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Jobs");

    // Define headers (including new columns)
    worksheet.columns = [
      { header: "Demand Status", key: "demandStatus", width: 15 },
      {
        header: "SOC",
        key: "onetSOC",
        width: 15,
        style: { numFmt: "@" }, // Text format
      },
      { header: "Title", key: "onetTitle", width: 30 },
      {
        header: "Annual Wage",
        key: "avgAnnualWage",
        width: 18,
        style: { numFmt: "$#,##0.00" }, // currency
      },
      {
        header: "Hourly Wage",
        key: "avgHourlyWage",
        width: 15,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Openings",
        key: "avgTotalOpenings",
        width: 15,
      },
      {
        header: "Employment",
        key: "estimatedEmployment",
        width: 15,
      },
      {
        header: "Growth Rate",
        key: "longtermGrowth",
        width: 15,
        // Format as percentage with two decimals (e.g. "2.45%")
        style: { numFmt: "0.00%" },
      },
      {
        header: "Education",
        key: "education",
        width: 25,
      },
    ];

    // Style the header row
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "a5f3fc" },
      };
      cell.border = {
        bottom: { style: "thin", color: { argb: "000000" } },
      };
    });

    // Insert data rows
    items.forEach((job) => {
      // Convert numeric fields to the actual numeric values
      const rowValues = {
        demandStatus: getDemandText(job.maxDemand) ?? "",
        onetSOC: job.onetSOC ?? "", // store as text

        onetTitle: job.onetTitle ?? "",

        // numeric wages
        avgAnnualWage: job.avgAnnualWage ?? 0,
        avgHourlyWage: job.avgHourlyWage ?? 0,

        // numeric openings & employment
        avgTotalOpenings: job.avgTotalOpenings ?? 0,
        estimatedEmployment: job.estimatedEmployment ?? 0,

        // numeric growth (0.0243 => 2.43%)
        longtermGrowth: job.longtermGrowth ?? 0,

        // text
        education: job.education ?? "",
      };

      worksheet.addRow(rowValues);
    });

    // Save to file
    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "JobsTable.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // If you want an “Export Success” or “Checkmark” animation, 
      // you can replicate your ExportButton logic here or call 
      // set some local state and revert after a timeout.
    } catch (err) {
      console.error("Error generating Excel:", err);
      // Could trigger an error animation similarly.
    }
  };

  // -------------------------------
  // Group Items by Education
  // -------------------------------
  const educationLevelOrder = [
    "Associate's degree",
    "Postsecondary nondegree award",
    "Bachelor's degree",
    "High school diploma or equivalent",
    "Some college, no degree",
    "No formal educational credential",
    "Master's degree",
    "Doctoral or professional degree",
    "NA",
  ];

  // Group them
  const groupedItems = items.reduce((acc, item) => {
    const edu = educationLevelOrder.includes(item.education) ? item.education : "NA";
    if (!acc[edu]) {
      acc[edu] = [];
    }
    acc[edu].push({
      ...item,
      education: edu,
      onetSOC: item.onetSOC || "Unknown SOC Code",
      onetTitle: item.onetTitle || "Unknown Job Title",
    });
    return acc;
  }, {});

  // Count total
  let totalOccupations = 0;
  educationLevelOrder.forEach((level) => {
    if (groupedItems[level]) {
      totalOccupations += groupedItems[level].length;
    }
  });

  const hasMoreOccupations = totalOccupations > visibleLimit;

  // We'll build two arrays:
  //  - `alwaysVisibleElements` (first 4 jobs total)
  //  - `expandableElements` (the rest)
  let occupationsRendered = 0;
  const alwaysVisibleElements = [];
  const expandableElements = [];
  const renderedGroups = new Set(); // track if we already put the education header

  // Simple variants
  const rowVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.2 } },
  };

  educationLevelOrder
    .filter((level) => groupedItems[level]?.length > 0)
    .forEach((educationLevel) => {
      const occupations = groupedItems[educationLevel];
      occupations.forEach((item) => {
        const isInFirstFour = occupationsRendered < visibleLimit;

        // Check if we need a group heading
        const groupHeader = (
          <div
            key={`header-${educationLevel}`}
            className="grid grid-cols-9 bg-zinc-50 border-b border-zinc-300"
          >
            <div className="col-span-9 font-semibold text-gray-500 text-left px-6 pt-11 pb-1">
              {educationLevel} (typical education needed)
            </div>
          </div>
        );

        // Decide which array
        if (isInFirstFour) {
          if (!renderedGroups.has(educationLevel)) {
            alwaysVisibleElements.push(groupHeader);
            renderedGroups.add(educationLevel);
          }
          alwaysVisibleElements.push(
            <div
              key={item.onetSOC}
              className="grid grid-cols-9 hover:bg-slate-50/60 border-b border-zinc-100"
            >
              {/* Renders your row-level component. E.g. <JobsTable jobs={item} /> */}
              <ItemComponent {...{ [resourceName]: item }} />
            </div>
          );
          occupationsRendered++;
        } else {
          if (!renderedGroups.has(educationLevel)) {
            expandableElements.push(
              <motion.div
                key={`header-${educationLevel}`}
                className="grid grid-cols-9 bg-zinc-50 border-b border-zinc-300"
                variants={rowVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div className="col-span-9 font-semibold text-gray-500 text-left px-6 pt-11 pb-1">
                  {educationLevel} (Typical Education for Entry)
                </div>
              </motion.div>
            );
            renderedGroups.add(educationLevel);
          }
          expandableElements.push(
            <motion.div
              key={item.onetSOC}
              className="grid grid-cols-9 hover:bg-slate-50/60 border-b last:border-b-0 border-zinc-100"
              variants={rowVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ItemComponent {...{ [resourceName]: item }} />
            </motion.div>
          );
        }
      });
    });

  return (
    <div className="mt-4 mb-6 pb-4 print:pr-10 z-20 rounded-lg overflow-hidden">
      {/* --- TOOLBAR: Copy & Export Buttons --- */}
      <div className='print:hidden px-3 flex justify-start items-center gap-x-4 mb-3 p-2'>
      <ListWorkforce changeFilter={changeFilter} /> 
                        </div>
      <div className="flex justify-end items-center gap-x-4 mb-3 p-1">
        {/* Copy Button */}
        <button
          onClick={copyItemsToClipboard}
          className="w-[70 px] relative rounded items-center inline-flex text-sm py-2 px-3 font-medium text-slate-800 hover:text-blue-600 bg-gray-100 outline outline-slate-300 outline-1 outline-offset-2 focus-visible:ring-2 focus-visible:ring-blue-200 focus-visible:ring-opacity-75"
        >
          {shareLinkCopied ? (
            <>
              <svg
                className="mr-2 h-4 w-4 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={4}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg
                className="mr-2 h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                stroke="currentColor" strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0
                  012-2h8a2 2 0 012 2v2m-6 12h8
                  a2 2 0 002-2v-8a2 2 0 00-2
                  -2h-8a2 2 0 00-2 2v8a2 2
                  0 002 2z"
                />
              </svg>
              Copy
            </>
          )}
        </button>

        {/* Export to Excel Button */}
        <button
          onClick={exportItemsToExcel}
          className="w-[166px] gap-2 relative rounded items-center inline-flex text-sm py-2 px-3 font-medium text-slate-800 hover:text-blue-600 bg-gray-100 outline outline-slate-300 outline-1 outline-offset-2 focus-visible:ring-2 focus-visible:ring-blue-200 focus-visible:ring-opacity-75"
        >
         <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" className="size-5">
  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 7.5h-.75A2.25 2.25 0 0 0 4.5 9.75v7.5a2.25 2.25 0 0 0 2.25 2.25h7.5a2.25 2.25 0 0 0 2.25-2.25v-7.5a2.25 2.25 0 0 0-2.25-2.25h-.75m0-3-3-3m0 0-3 3m3-3v11.25m6-2.25h.75a2.25 2.25 0 0 1 2.25 2.25v7.5a2.25 2.25 0 0 1-2.25 2.25h-7.5a2.25 2.25 0 0 1-2.25-2.25v-.75" />
</svg>

          Export to Excel
        </button>
      </div>  

      {/* --- HEADERS / ALWAYS VISIBLE ROWS / EXPANDABLE ROWS --- */}
      <div className="overflow-x-auto relative">
        <div className="relative w-full text-sm text-left text-zinc-500">

{/* Table Header Row */}
<div className="grid grid-cols-9 bg-zinc-100 text-slate-700 uppercase">
  <div className="font-semibold px-4 pt-4 pb-1">Demand / SOC</div>
  <div className="col-span-3 font-semibold px-4 pt-4 pb-1">Job Title</div>
  <div className="font-semibold px-3 pt-4 pb-1">Avg. Salary</div>
  <div className="font-semibold px-3 pr-3 pt-4 pb-1">Annual Openings (’21-’31)</div>
  <div className="font-semibold px-3 pt-4 pb-1">Growth Rate</div>
  <div className="font-semibold px-3 pt-4 pb-1">Employment</div>
  <div className="font-semibold px-3 pt-4 pb-1 print:hidden text-end">Links</div>
</div>

          {/* Always-visible rows */}
          <div>{alwaysVisibleElements}</div>

          {/* AnimatePresence for expanded rows */}
          <AnimatePresence initial={false}>
            {expanded && (
              <AnimatedHeight isOpen={expanded}>
                <div>{expandableElements}</div>
              </AnimatedHeight>
            )}
          </AnimatePresence>
        </div>

        {/* The fade-out gradient for collapsed mode */}
        {!expanded && hasMoreOccupations && (
          <div className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none bg-gradient-to-t from-white to-transparent" />
        )}
      </div>

      {/* Expand / Collapse Button */}
      {hasMoreOccupations && (
        <button
          className="w-full flex flex-col items-center justify-center mt-3 py-6 bg-zinc-50 rounded-b-lg bg-gradient-to-b from-white to-transparent hover:bg-none hover:bg-zinc-100/40 transition duration-200 ease-in-out group border-b-4 border-zinc-100 hover:border-zinc-100"
          onClick={() => setExpanded(!expanded)}
        >
          <div
            className={`flex items-center justify-center w-12 h-12 bg-white group-hover:bg-zinc-200/70 rounded-full text-blue-600 transition-transform duration-200 ease-in-out ${
              expanded ? "rotate-180" : ""
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth={2}
              className="w-6 h-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <span className="mt-2 text-blue-600 font-medium transition duration-200 group-hover:underline">
            {expanded ? "Show Less" : "Show More"}
          </span>
        </button>
      )}
    </div>
  );
};